import { BrandKitFormFields, BrandKitFormInputs } from '../BrandKitForm.types'
import {
  Clear as ClearIcon,
  FilePresentSharp as FilePresentSharpIcon,
  Upload as UploadIcon
} from '@mui/icons-material'
import { FC, useState } from 'react'
import { PickerButton } from './PickerButton'
import { ProcessedFileFormats } from '@dtx-company/design-system/src/components/FileUpload/FileUpload'
import { UseBrandKitFormReturn, useBrandKitLogoFilename } from '../BrandKitForm.hooks'
import { uploadFile } from '@app/common/src/utils/uploadFile'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useFileUpload } from '@dtx-company/design-system/src/components/FileUpload/FileUpload.hooks'
import { useFormContext } from 'react-hook-form'

export const LogoUpload: FC<{ onLogoChanged: UseBrandKitFormReturn['onLogoChanged'] }> = ({
  onLogoChanged
}) => {
  const accept = 'image/jpeg,image/png'
  const { jwt } = useAuthState()
  const { setValue, register, resetField } = useFormContext<BrandKitFormInputs>()
  const brandKitLogoFilename = useBrandKitLogoFilename()

  /**
   * Keep track of whether the user has cleared the input field. This is used to determine
   * whether to display the filename or not in our `displayValue` assignment below.
   */
  const [isRemoved, setIsRemoved] = useState(false)

  const onChangeHandler = async (file: ProcessedFileFormats): Promise<void> => {
    onLogoChanged(file)
    if (file instanceof File) {
      setIsRemoved(false)
      // upload logoImage file if it exists
      const uploadedData = await uploadFile(file, {}, jwt)
      if (uploadedData) {
        const cdnUrl = new URL(uploadedData.cdn_url)
        // delete 'ts' query param from cdnUrl
        cdnUrl.searchParams.delete('ts')
        setValue(BrandKitFormFields.LogoImage, cdnUrl.toString(), { shouldValidate: true })
      }
    } else {
      setIsRemoved(true)
    }
  }

  const { fileName, hiddenRef, handleFileSelection, clearFileSelection } = useFileUpload({
    onChange: onChangeHandler,
    accept
  })

  const displayValue = isRemoved ? undefined : fileName || brandKitLogoFilename

  const { ref, ...logoProps } = register(BrandKitFormFields.LogoImage, {
    onChange: handleFileSelection
  })

  const openFilePicker = (): void => {
    hiddenRef.current?.click()
  }

  const resetLogo = (): void => {
    clearFileSelection()
    resetField(BrandKitFormFields.LogoImage)
  }

  return (
    <PickerButton
      data-testid="brand-kit-form__logo-field-wrapper"
      hasValue={!!displayValue}
      onClick={displayValue ? resetLogo : openFilePicker}
      buttonStartIcon={FilePresentSharpIcon}
      startIcon={<UploadIcon />}
      endIcon={<ClearIcon />}
      displayValue={displayValue}
      label="Upload logo"
    >
      <input
        type="file"
        hidden
        accept={accept}
        ref={el => {
          hiddenRef.current = el
          ref(el)
        }}
        {...logoProps}
      />
    </PickerButton>
  )
}
